import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Footer from '../Layout/Footer';

const PhoneVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  const phone = sessionStorage.getItem('verification_phone');
  const userId = sessionStorage.getItem('temp_user_id');

  useEffect(() => {
    if (!phone || !userId) {
      navigate('/register');
      return;
    }

    if (timeLeft > 0 && !canResend) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timeLeft, canResend, phone, userId, navigate]);

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerify = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('http://localhost:5000/api/auth/verify-phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp: otpCode }),
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Phone verified successfully! Please login.');
        sessionStorage.removeItem('verification_phone');
        sessionStorage.removeItem('temp_user_id');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.error(data.message || 'Verification failed');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await fetch('http://localhost:5000/api/auth/resend-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();

      if (data.success) {
        toast.success('New OTP sent to your phone');
        setTimeLeft(300);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
      } else {
        toast.error(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="auth-page">
        <div className="auth-shell auth-shell-single">
          <div className="auth-card auth-card-centered">
            <div className="auth-card-header text-center">
              <div className="auth-brand-badge auth-brand-badge-centered">Coinspire</div>
              <p className="auth-kicker">Phone Verification</p>
              <h2 className="auth-card-title">Verify your phone number</h2>
              <p className="auth-card-subtitle">
                We sent a 6-digit verification code to
              </p>
              <p className="auth-phone-highlight">{phone}</p>
            </div>

            <div className="auth-otp-row">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="auth-otp-input"
                  disabled={loading}
                />
              ))}
            </div>

            <div className="auth-verification-meta">
              {!canResend ? (
                <p className="auth-timer-text">
                  Code expires in <span className="auth-timer-value">{formatTime(timeLeft)}</span>
                </p>
              ) : (
                <button
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="auth-inline-link"
                >
                  Resend verification code
                </button>
              )}
            </div>

            <button onClick={handleVerify} disabled={loading} className="auth-submit-btn">
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>

            <p className="auth-small-note">
              Didn&apos;t receive the code? Check your phone number or try again.
            </p>

            <button
              onClick={() => navigate('/register')}
              className="auth-back-btn"
              disabled={loading}
            >
              ← Go back to registration
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PhoneVerification;